// Editar varios productos a la vez desde la lista de Tienda online, en Tiendanube y en Mercado Libre
// (ADR-2026-10-30-edicion-masiva). Se escribe en la ficha de cada uno (la misma de editarlo a mano) y se
// sincroniza sólo a ésos: es lo mismo que abrir cada producto, cambiarlo y tocar Sincronizar.

import {
  actualizarFicha,
  actualizarVariante,
  fetchLanzamiento,
  guardarSeccionesDestrabadas,
  guardarSincronizarEn,
  type CamposDeFicha,
  type ProductoDeLanzamiento,
} from "./lanzamientosApi";
import type { Visibilidad } from "./camposDeLaTienda";
import {
  filaDeProductoDeTiendanube,
  type ProductoDeLaTienda,
} from "./productosDeLaTienda";
import { conDestrabadas, type SeccionTrabable } from "./seccionesTrabadas";
import {
  sincronizarLaTienda,
  type ResultadoDeSincronizar,
} from "./sincronizarTienda";
import type { TiendasDelProducto } from "./sincronizarEnVarias";

export interface EdicionMasiva {
  /** Fijar un precio, o subirlo o bajarlo un porcentaje (negativo = bajar). */
  precio?: { modo: "fijar" | "porcentaje"; valor: number };
  /** El stock de cada variante (o del producto, si no tiene). */
  stock?: number;
  visibilidad?: Visibilidad;
  envioGratis?: boolean;
  /** En qué tiendas se aplica. */
  tiendas: TiendasDelProducto;
}

const redondear = (n: number) => Math.round(n * 100) / 100;
const aNumero = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

/** El precio que queda: fijo, o el de antes más/menos el porcentaje. null = no se puede calcular (no tiene). */
export function precioNuevo(
  actual: string | number | null | undefined,
  e: NonNullable<EdicionMasiva["precio"]>,
): number | null {
  if (e.modo === "fijar") return e.valor > 0 ? redondear(e.valor) : null;
  const antes = aNumero(actual);
  if (antes === null || antes <= 0) return null;
  const n = redondear(antes * (1 + e.valor / 100));
  return n > 0 ? n : null;
}

/**
 * Lo que la edición escribe en UNA ficha: los campos del producto, los de cada variante y las secciones
 * que hay que destrabar (si no, una sección trabada guarda el cambio pero no lo manda). Puro.
 */
export function cambiosDeLaFicha(
  p: Pick<ProductoDeLanzamiento, "precio" | "precioPorVariante" | "variantes">,
  e: EdicionMasiva,
): {
  campos: CamposDeFicha;
  variantes: Array<{ id: string; campos: { precio?: string; stock?: string } }>;
  secciones: SeccionTrabable[];
} {
  const campos: CamposDeFicha = {};
  const porVariante = new Map<string, { precio?: string; stock?: string }>();
  const secciones = new Set<SeccionTrabable>();
  const deVariante = (id: string) =>
    porVariante.get(id) ?? porVariante.set(id, {}).get(id)!;

  if (e.precio) {
    const general = precioNuevo(p.precio, e.precio);
    if (general !== null) {
      campos.precio = String(general);
      secciones.add("precio");
    }
    // Con precio por variante, cada una el suyo (con porcentaje, sobre el que tenía).
    if (p.precioPorVariante) {
      for (const v of p.variantes) {
        const n = precioNuevo(v.precio ?? p.precio, e.precio);
        if (n !== null) deVariante(v.id).precio = String(n);
      }
      if (p.variantes.length > 0) secciones.add("variantes");
    }
  }
  if (typeof e.stock === "number") {
    const n = Math.max(0, Math.floor(e.stock));
    if (p.variantes.length > 0) {
      for (const v of p.variantes) deVariante(v.id).stock = String(n);
      secciones.add("variantes");
    } else {
      campos.stock = n;
      secciones.add("inventario");
    }
  }
  if (e.visibilidad) {
    campos.visibilidad = e.visibilidad;
    secciones.add("visibilidad");
  }
  if (typeof e.envioGratis === "boolean") {
    campos.envioGratis = e.envioGratis;
    secciones.add("envioGratis");
  }
  return {
    campos,
    variantes: [...porVariante].map(([id, c]) => ({ id, campos: c })),
    secciones: [...secciones],
  };
}

/** ¿La edición cambia algo? */
export const hayEdicion = (e: EdicionMasiva) =>
  !!e.precio ||
  typeof e.stock === "number" ||
  !!e.visibilidad ||
  typeof e.envioGratis === "boolean";

export async function aplicarEdicionMasiva(p: {
  productos: ProductoDeLaTienda[];
  edicion: EdicionMasiva;
  companyId: string;
  brandId: string | null;
  userId: string | undefined;
  ml: { disponible: boolean; conectada: boolean };
  alAvanzar?: (texto: string) => void;
}): Promise<ResultadoDeSincronizar> {
  // 1. Cada uno en la lista de cambios de la tienda (lo trae a Atlas si hace falta). Uno que no se puede
  // traer no frena a los demás: se cuenta (review Greptile de #232).
  const fallas: string[] = [];
  let lanzamientoId: string | null = null;
  const filas: string[] = [];
  for (const [i, producto] of p.productos.entries()) {
    p.alAvanzar?.(`Preparando ${i + 1} de ${p.productos.length}…`);
    try {
      const r = await filaDeProductoDeTiendanube(producto, {
        userId: p.userId,
        companyId: p.companyId,
        brandId: p.brandId,
      });
      lanzamientoId = r.lanzamientoId;
      if (r.filaId) filas.push(r.filaId);
      else fallas.push(producto.nombre);
    } catch {
      fallas.push(producto.nombre);
    }
  }
  if (!lanzamientoId || filas.length === 0)
    return {
      texto: "No pudimos preparar los productos. Probá de nuevo.",
      error: true,
    };

  // 2. Los cambios en la ficha de cada uno, destrabando lo que se toca y con las tiendas elegidas. Si algo no
  // se guarda, ese producto no se sincroniza: con lo guardado a medias (o con las tiendas de antes) podía
  // llegar a una tienda que la persona no eligió.
  const detalle = await fetchLanzamiento(lanzamientoId, p.companyId);
  const tiendas = (["tiendanube", "mercadolibre"] as const).filter(
    (t) => p.edicion.tiendas[t],
  );
  const guardadas: string[] = [];
  for (const fila of detalle?.productos_.filter((x) => filas.includes(x.id)) ??
    []) {
    const c = cambiosDeLaFicha(fila, p.edicion);
    const errores = [
      Object.keys(c.campos).length > 0
        ? (await actualizarFicha(fila.id, c.campos)).error
        : null,
      ...(
        await Promise.all(
          c.variantes.map((v) => actualizarVariante(v.id, v.campos)),
        )
      ).map((x) => x.error),
      c.secciones.length > 0
        ? (
            await guardarSeccionesDestrabadas(
              fila.id,
              conDestrabadas(fila.seccionesDestrabadas, c.secciones),
            )
          ).error
        : null,
      (await guardarSincronizarEn(fila.id, tiendas)).error,
    ];
    if (errores.some(Boolean)) fallas.push(fila.nombre);
    else guardadas.push(fila.id);
  }
  const conFallas = (r: ResultadoDeSincronizar): ResultadoDeSincronizar =>
    fallas.length === 0
      ? r
      : {
          ...r,
          texto: `${r.texto} No se pudo ${fallas.length === 1 ? `cambiar ${fallas[0]}` : `cambiar ${fallas.length} productos (${fallas.slice(0, 3).join(", ")}${fallas.length > 3 ? "…" : ""})`}.`,
          error: true,
        };
  if (guardadas.length === 0)
    return conFallas({ texto: "No se guardó ningún cambio.", error: true });

  // 3. Sincronizar sólo ésos, en las tiendas elegidas.
  p.alAvanzar?.("Sincronizando…");
  return conFallas(
    await sincronizarLaTienda({
      lanzamientoId,
      companyId: p.companyId,
      brandId: p.brandId,
      ml: p.ml,
      soloFilas: guardadas,
      crearEnMl: false,
      alAvanzar: (subidas, total) =>
        p.alAvanzar?.(`Sincronizando ${subidas}/${total}`),
    }),
  );
}
